import React, { useEffect, useState, useRef } from 'react';
import {
  collection,
  query,
  where,
  getDoc,
  getDocs,
  doc,
  orderBy,
  addDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';

const Messages = React.memo(() => {
  const [groupedMessages, setGroupedMessages] = useState({});
  const [expandedApp, setExpandedApp] = useState(null);
  const [newMessages, setNewMessages] = useState({});
  const currentUserId = auth.currentUser?.uid; // guarded — page will bail out below if null
  const chatContainerRef = useRef(null);



  useEffect(() => {
    if (!currentUserId) return;

    const markMessagesViewed = async () => {
      try {
        await updateDoc(doc(db, 'users', currentUserId), {
          lastMessagesViewedAt: serverTimestamp(),
        });
      } catch (error) {
        console.error('Error updating message view timestamp:', error);
      }
    };

    markMessagesViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  useEffect(() => {
    const fetchMessagesAndApplications = async () => {
      if (!currentUserId) return;
      // Query separately as parent and as sitter so each satisfies the participant
      // filter required by the new applications read rule (no OR support in older
      // Firestore SDK; two queries + merge is guaranteed to work).
      const [parentSnap, sitterSnap] = await Promise.all([
        getDocs(query(collection(db, 'applications'), where('parentId', '==', currentUserId), where('status', 'in', ['Active', 'Completed']))),
        getDocs(query(collection(db, 'applications'), where('sitterId', '==', currentUserId), where('status', 'in', ['Active', 'Completed']))),
      ]);

      const relevantAppsMap = new Map();
      parentSnap.forEach(docSnap => relevantAppsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() }));
      sitterSnap.forEach(docSnap => relevantAppsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() }));
      const relevantApps = Array.from(relevantAppsMap.values());

      const appIds = relevantApps.map(app => app.id);

      // If no relevant applications, set empty and return early
      if (appIds.length === 0) {
        setGroupedMessages({});
        return;
      }

      // Only fetch messages for relevant applications
      const messagesSnap = await getDocs(
        query(
          collection(db, 'messages'),
          where('applicationId', 'in', appIds),
          orderBy('timestamp', 'asc')
        )
      );

      // Batch fetch user info for all unique user IDs
      const uniqueUserIds = [...new Set(relevantApps.flatMap(app => [app.parentId, app.sitterId]))];
      const userInfoPromises = uniqueUserIds.map(async (userId) => {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          return {
            userId,
            name: userData.name || 'Unknown',
            profilePictureURL: userData.profilePictureURL || null,
          };
        }
        return { userId, name: 'Unknown', profilePictureURL: null };
      });

      const userInfoResults = await Promise.all(userInfoPromises);
      const userInfoMap = userInfoResults.reduce((acc, user) => {
        acc[user.userId] = { name: user.name, profilePictureURL: user.profilePictureURL };
        return acc;
      }, {});

      const messages = messagesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));



      const grouped = {};
      for (const app of relevantApps) {
        const otherUserId = app.parentId === currentUserId ? app.sitterId : app.parentId;
        const otherUserInfo = userInfoMap[otherUserId] || { name: 'Unknown', profilePictureURL: null };
        grouped[app.id] = {
          jobTitle: app.jobTitle,
          startDate: app.jobStartDate,
          recipientId: otherUserId, // used when sending a message
          otherUser: app.parentId === currentUserId
            ? { name: app.sitterName, photo: otherUserInfo.profilePictureURL }
            : { name: app.parentName, photo: otherUserInfo.profilePictureURL },
          messages: []
        };
      }

      for (const msg of messages) {
        if (grouped[msg.applicationId]) {
          grouped[msg.applicationId].messages.push(msg);
        }
      }

      const sorted = Object.fromEntries(
        Object.entries(grouped).sort(([, a], [, b]) =>
          new Date(a.startDate) - new Date(b.startDate)
        )
      );

      setGroupedMessages(sorted);
    };

    fetchMessagesAndApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  const handleSendMessage = async (applicationId) => {
    const message = newMessages[applicationId]?.trim();
    if (!message) return;

    const recipientId = groupedMessages[applicationId]?.recipientId || null;

    try {
      const newMessageDoc = await addDoc(collection(db, 'messages'), {
        applicationId,
        senderId: currentUserId,
        recipientId, // needed so the recipient (and notification email) can be resolved
        message,
        timestamp: serverTimestamp(),
      });

      const newMessage = {
        id: newMessageDoc.id,
        applicationId,
        senderId: currentUserId,
        recipientId,
        message,
        timestamp: new Date(),
      };

      setGroupedMessages((prev) => {
        const updated = { ...prev };
        if (updated[applicationId]) {
          updated[applicationId] = {
            ...updated[applicationId],
            messages: [...updated[applicationId].messages, newMessage],
          };
        }
        return updated;
      });

      setNewMessages(prev => ({ ...prev, [applicationId]: '' }));
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please check your connection and try again.');
      return;
    }
  
    // Scroll to bottom
    // Use requestAnimationFrame for smoother scrolling
    requestAnimationFrame(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Your Conversations</h1>
          <p className="text-gray-600 text-lg">Stay connected with your job connections</p>
        </div>

        {Object.entries(groupedMessages).length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 max-w-md mx-auto">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No conversations yet</h3>
              <p className="text-gray-600">Start a job to begin messaging with sitters or parents.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedMessages).map(([applicationId, data]) => (
              <div key={applicationId} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Conversation Header */}
                <button
                  className="w-full text-left px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-colors duration-150 border-b border-gray-200"
                  onClick={() =>
                    setExpandedApp(prev => (prev === applicationId ? null : applicationId))
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        {data.otherUser.photo ? (
                          <img
                            src={data.otherUser.photo}
                            alt={data.otherUser.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-lg shadow-sm">
                            {data.otherUser.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 border-2 border-white rounded-full"></div>
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-gray-900 text-lg">{data.otherUser.name}</p>
                        <div className="text-sm text-gray-600">
                          <p className="font-medium">{data.jobTitle}</p>
                          <p className="text-xs text-gray-500">
                            Starts {new Date(data.startDate).toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 font-medium">
                        {data.messages.length} message{data.messages.length !== 1 ? 's' : ''}
                      </span>
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-bold text-lg">
                          {expandedApp === applicationId ? '−' : '+'}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Chat Messages */}
                {expandedApp === applicationId && (
                  <div className="p-6 bg-gray-50">
                    <div
                      ref={chatContainerRef}
                      className="max-h-96 overflow-y-auto space-y-4 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 will-change-scroll"
                    >
                      {data.messages.map((msg, index) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.senderId === currentUserId ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[75%] px-4 py-3 rounded-2xl shadow-sm ${
                              msg.senderId === currentUserId
                                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-br-md'
                                : 'bg-white text-gray-800 rounded-bl-md border border-gray-200'
                            }`}
                          >
                            <p className="text-sm leading-relaxed">{msg.message}</p>
                            <p className={`text-xs mt-2 text-right ${
                              msg.senderId === currentUserId 
                                ? 'text-blue-100' 
                                : 'text-gray-500'
                            }`}>
                              {msg.timestamp?.toDate
                                ? msg.timestamp.toDate().toLocaleTimeString('en-US', { 
                                    hour: 'numeric', 
                                    minute: '2-digit',
                                    hour12: true 
                                  })
                                : new Date().toLocaleTimeString('en-US', { 
                                    hour: 'numeric', 
                                    minute: '2-digit',
                                    hour12: true 
                                  })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Message Input */}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            placeholder="Type your message..."
                            value={newMessages[applicationId] || ''}
                            onChange={(e) =>
                              setNewMessages(prev => ({ ...prev, [applicationId]: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSendMessage(applicationId);
                            }}
                            className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-150 bg-white shadow-sm"
                          />
                          <button
                            onClick={() => handleSendMessage(applicationId)}
                            disabled={!newMessages[applicationId]?.trim()}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-150 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Press Enter to send or click the send button
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

export default Messages;
